/**
 * useShellStealth - Stealth's ladder on this screen: the state the frame
 * holds, who the opposition is, who among them is sharpest, and the
 * director's verbs.
 *
 * The ladder is part of the phase frame (`frame.stealth`), so every verb
 * is one `direction.direct` - the GM's write, here at once and on the wire,
 * and restored by undo with the frame. `canDirect` is who may: the GM, or
 * anyone outside a session.
 *
 * @param {object} s - the shell scope: direction, roster, actor, sessionId, isGm, addRollNarrative, connectedPlayers
 *   (the characters the players' seats act as set the party's side).
 * @returns {{ stealth: { state, canDirect, opposition, sharpest, setRung, setLine, setOpposition, setLearned, apply }, quietMoveRef, attackHeardRef }}
 */
import { useCallback, useMemo, useRef } from 'react';
import { stealthOf } from '../engine/phase/frame';
import { actingByFrom } from '../engine/roster/actingBy';
import { toMs } from '../utils/timestamps';
import {
  RUNG_LABELS,
  lineOf,
  withLearned,
  withLine,
  withOpposition,
  withRung,
  withoutHidden,
} from '../engine/stealth/ladder';
import { oppositionFor, sharpest as sharpestOf } from '../engine/stealth/opposition';
import { watchingOf } from '../engine/stealth/sight';
import { alertWords } from '../engine/stealth/alerts';

const NONE = new Set();
const NO_ONE = [];

/**
 * The characters the PLAYERS' seats act as - never a GM's, who acts as anyone - by id, stable while they are the
 * same: the seats list is new with every heartbeat. Stealth takes the party's side from them before this screen's own
 * actor (2026-10-28). Not `useShellFight`'s list on purpose: that one counts the GM's seat too, which a solo table
 * (one person, the GM) needs for its own character.
 */
function usePlayed(connectedPlayers) {
  const key = Object.values(
    actingByFrom(
      (connectedPlayers ?? NO_ONE).filter((p) => !p.isGm),
      toMs,
    ),
  )
    .sort()
    .join('|');
  return useMemo(() => (key ? key.split('|') : NO_ONE), [key]);
}

export default function useShellStealth(s) {
  const { direction, roster } = s;
  const state = stealthOf(direction.frame);
  const canDirect = !s.sessionId || !!s.isGm;
  const partyTeam = direction.frame?.partyTeam ?? null;
  const actorId = s.actor?.id ?? null;
  const played = usePlayed(s.connectedPlayers);
  const opposition = useMemo(
    () => oppositionFor(roster, partyTeam, actorId, played),
    [roster, partyTeam, actorId, played],
  );
  const sharpest = useMemo(() => sharpestOf(opposition, state), [opposition, state]);
  // Who is watching, with their Dark Sight: what the display's wedges are drawn for (plan map-display, H).
  const watching = useMemo(() => watchingOf(opposition, state), [opposition, state]);
  // Each roused watcher's word on its figure (Z2), memoized: the markers take a new object for news.
  const alerts = useMemo(
    () =>
      alertWords(
        state,
        opposition.map((c) => c.id),
      ),
    [state, opposition],
  );

  const hiddenIds = s.hidden?.hiddenIds ?? NONE;
  const live = useRef({ direction, canDirect, addRollNarrative: s.addRollNarrative, hiddenIds });
  live.current = { direction, canDirect, addRollNarrative: s.addRollNarrative, hiddenIds };
  /** Apply one change to the ladder as it stands now. Only the director's does anything. */
  const apply = useCallback((change) => {
    const now = live.current;
    if (!now.canDirect) return;
    const was = stealthOf(now.direction.frame);
    // No hidden figure is ever named in what every player receives: dropped from every write.
    const next = withoutHidden(change(was), now.hiddenIds);
    if (next !== was) now.direction.direct({ stealth: next });
  }, []);
  const { setRung, setLine, setOpposition, setLearned } = useLadderVerbs(live, apply);

  // Where the drum's quiet plates are sent. Filled by `useShellQuietMoves`, which runs in a later
  // domain (it needs the seats and the vox); read at press time, so the order does not matter.
  const quietMoveRef = useRef(null);
  // And where an attack made in Stealth is reported, by the same late binding.
  const attackHeardRef = useRef(null);

  const stealth = useMemo(
    () => ({
      state,
      canDirect,
      opposition,
      sharpest,
      watching,
      alerts,
      hiddenIds,
      setRung,
      setLine,
      setOpposition,
      setLearned,
      apply,
    }),
    [
      state,
      canDirect,
      opposition,
      sharpest,
      watching,
      alerts,
      hiddenIds,
      setRung,
      setLine,
      setOpposition,
      setLearned,
      apply,
    ],
  );
  return { stealth, quietMoveRef, attackHeardRef };
}

/** The ladder's verbs, through `apply`: move it (and say so in the log), its line, a watcher's state, the page. */
function useLadderVerbs(live, apply) {
  /** Move the ladder, and say so in the log: the table reads the rung's line there as well as on the ladder. */
  const setRung = useCallback(
    (rung) => {
      const now = live.current;
      if (!now.canDirect || stealthOf(now.direction.frame).rung === rung) return;
      // The line the table reads at the new rung: the GM's own for it, when they have written one.
      const line = lineOf(withRung(stealthOf(now.direction.frame), rung));
      apply((st) => withRung(st, rung));
      now.addRollNarrative?.({
        type: 'info',
        lane: 'story',
        text: `Stealth: ${RUNG_LABELS[rung]}. ${line}`,
        timestamp: Date.now(),
      });
    },
    [apply, live],
  );
  const setLine = useCallback((line) => apply((st) => withLine(st, line)), [apply]);
  const setOpposition = useCallback((id, to) => apply((st) => withOpposition(st, id, to)), [apply]);
  const setLearned = useCallback((text) => apply((st) => withLearned(st, text)), [apply]);
  return { setRung, setLine, setOpposition, setLearned };
}
